import React, { useState } from "react";
import { SFStack } from "../../grid";
import { SFTuneIcon, SFFilterListIcon } from "../../icons";
import SFBadge from "../../badge";
import TuneFilter from "./tuneFilter";
import SearchFilter from "./searchFilter";
import FilterPanel from "./filterPanel";
import { CheckboxFilterPropTypes } from "./checkboxFilter";
import Box from "@mui/material/Box";
import SFIconButton from "../../button";
import getStyled from "../../../hooks/getStyled";
import PropTypes from "prop-types";
import FilterChipList from "./filterChipList";

const { styled } = getStyled();

const Wrapper = styled("div")(({ theme }) => ({
  position: "relative",
  width: "100%",
}));

const StyledButton = styled(SFIconButton)(({ theme }) => ({
  padding: theme.spacing(1.7),
  width: "46px",
  minWidth: "auto",
}));

const StyledStack = styled(SFStack)(({ theme }) => ({
  position: "absolute",
  zIndex: 10,
  backgroundColor: "white",
  width: "100%",
  borderLeft: `1px solid ${theme.palette.grey200.main}`,
  borderRight: `1px solid ${theme.palette.grey200.main}`,
  borderBottom: `1px solid ${theme.palette.grey200.main}`,
  borderBottomLeftRadius: "4px",
  borderBottomRightRadius: "4px",
}));

const StyledBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== "filterExpanded",
})(({ theme, filterExpanded }) => ({
  width: "100%",
  height: "100%",
  borderTopLeftRadius: "4px",
  borderTopRightRadius: "4px",
  backgroundColor: theme.palette.grey100.main,
  borderLeft: `1px solid ${theme.palette.grey200.main}`,
  borderRight: `1px solid ${theme.palette.grey200.main}`,
  borderTop: `1px solid ${theme.palette.grey200.main}`,
  borderBottom: filterExpanded.left
    ? "none"
    : `1px solid ${theme.palette.grey400.main}`,
}));

const LeftButtonContainer = styled(SFStack, {
  shouldForwardProp: (prop) => prop !== "filterExpanded",
})(({ theme, filterExpanded }) => ({
  height: "100%",
  borderTopLeftRadius: "4px",
  backgroundColor: filterExpanded.left
    ? theme.palette.white.main
    : theme.palette.grey100.main,
  borderRight: `1px solid ${theme.palette.grey200.main}`,
  borderBottom: filterExpanded.left
    ? `1px solid ${theme.palette.white.main}`
    : "none",
}));

const CenterContentContainer = styled(SFStack, {
  shouldForwardProp: (prop) => prop !== "filterExpanded",
})(({ theme, filterExpanded }) => ({
  height: "53px",
  width: "100%",
  borderBottom: filterExpanded.left
    ? `1px solid ${theme.palette.grey200.main}`
    : "none",
}));

const RightButtonContainer = styled(SFStack, {
  shouldForwardProp: (prop) => prop !== "filterExpanded",
})(({ theme, filterExpanded }) => ({
  height: "100%",
  borderTopRightRadius: "4px",
  backgroundColor: theme.palette.grey200.main,
  borderBottom: filterExpanded.left
    ? `1px solid ${theme.palette.grey200.main}`
    : "none",
}));

const SFPanelSearch = ({
  tuneFilter,
  searchFilter,
  dropdownFilter,
  checkboxFilter,
  onChange,
}) => {
  const [filterExpanded, setFilterExpanded] = useState({
    left: false,
    right: false,
  });

  const handleLeftExpand = () => {
    setFilterExpanded({ left: !filterExpanded.left, right: false });
  };

  const handleRightExpand = () => {
    setFilterExpanded({ left: false, right: !filterExpanded.right });
  };

  // Calculate the total number of selected filters
  const calculateTotalSelectedFilters = (dropdownFilter, checkboxFilter) => {
    // Count the selected values from dropdown/generic search
    const selectedValuesCount = dropdownFilter?.selectedValues?.length || 0;

    // Count the checked checkboxes
    let checkedCheckboxesCount = 0;
    if (
      checkboxFilter?.checkboxes &&
      Array.isArray(checkboxFilter.checkboxes)
    ) {
      checkboxFilter.checkboxes.forEach((checkbox) => {
        if (checkbox.checked === true) {
          checkedCheckboxesCount++;
        }
      });
    }

    // Return the total count
    return selectedValuesCount + checkedCheckboxesCount;
  };

  // Check if dropdownFilter or checkboxFilter are provided and have values
  const hasDropdownFilter =
    dropdownFilter &&
    Array.isArray(dropdownFilter.values) &&
    dropdownFilter.values.length > 0;

  const hasCheckboxFilter =
    checkboxFilter &&
    Array.isArray(checkboxFilter.checkboxes) &&
    checkboxFilter.checkboxes.length > 0;

  const hasFilters = hasDropdownFilter || hasCheckboxFilter;

  return (
    <SFStack spacing={3}>
      <Wrapper>
        <StyledBox filterExpanded={filterExpanded}>
          <SFStack
            direction="row"
            justifyContent={"space-between"}
            alignItems={"center"}
            sx={{ height: "100%" }}
          >
            <LeftButtonContainer filterExpanded={filterExpanded}>
              <StyledButton variant="trasparent" onClick={handleLeftExpand}>
                {<SFTuneIcon color="grey450" />}
              </StyledButton>
            </LeftButtonContainer>
            <CenterContentContainer
              alignItems={"center"}
              justifyContent={"center"}
              filterExpanded={filterExpanded}
            >
              <SearchFilter searchFilter={searchFilter} />
            </CenterContentContainer>
            {hasFilters && (
              <RightButtonContainer filterExpanded={filterExpanded}>
                <StyledButton variant="trasparent" onClick={handleRightExpand}>
                  <SFBadge
                    badgeContent={calculateTotalSelectedFilters(
                      dropdownFilter,
                      checkboxFilter
                    )}
                    color="info"
                  >
                    <SFFilterListIcon color="grey450" />
                  </SFBadge>
                </StyledButton>
              </RightButtonContainer>
            )}
          </SFStack>
        </StyledBox>
        {filterExpanded.left && (
          <StyledStack>
            <TuneFilter tuneFilter={tuneFilter} />
          </StyledStack>
        )}
        {hasFilters && filterExpanded.right && (
          <StyledStack>
            <FilterPanel
              dropdownFilter={dropdownFilter}
              checkboxFilter={checkboxFilter}
              onChange={onChange}
            />
          </StyledStack>
        )}
      </Wrapper>
      <FilterChipList
        checkboxFilter={checkboxFilter}
        onChange={onChange}
        onEditFilters={handleRightExpand}
      />
    </SFStack>
  );
};

export default SFPanelSearch;

/**
 * PropTypes definition for the SFPanelSearch component.
 * This component provides a comprehensive search interface with various filtering options.
 */
SFPanelSearch.propTypes = {
  /**
   * Array of tune filter options displayed when the tune button is clicked.
   * Each filter has its own onChange handler for direct state management.
   */
  tuneFilter: PropTypes.arrayOf(
    PropTypes.shape({
      /**
       * Unique identifier for the checkbox.
       */
      name: PropTypes.string.isRequired,

      /**
       * Whether the checkbox is checked.
       */
      checked: PropTypes.bool.isRequired,

      /**
       * Handler function called when the checkbox state changes.
       * @param {object} event - The change event from the checkbox.
       */
      onChange: PropTypes.func.isRequired,

      /**
       * Display text for the checkbox.
       */
      label: PropTypes.string.isRequired,
    })
  ).isRequired,

  /**
   * Configuration for the search input field.
   */
  searchFilter: PropTypes.shape({
    /**
     * Placeholder text displayed in the search field when empty.
     */
    placeholder: PropTypes.string.isRequired,

    /**
     * Handler function called when the search input changes.
     * @param {string} value - The new search text.
     */
    onChange: PropTypes.func.isRequired,

    /**
     * Current search term value.
     */
    searchTerm: PropTypes.string.isRequired,
  }).isRequired,

  /**
   * Configuration for the dropdown filter displayed in the filter panel.
   * Uses SFGenericSearch component to provide multi-select functionality.
   */
  dropdownFilter: PropTypes.shape({
    /**
     * Placeholder text displayed in the dropdown when no items are selected.
     */
    placeholder: PropTypes.string.isRequired,

    /**
     * Array of currently selected values in the dropdown.
     */
    selectedValues: PropTypes.arrayOf(PropTypes.string).isRequired,

    /**
     * Array of all available options for the dropdown.
     */
    values: PropTypes.arrayOf(PropTypes.string).isRequired,
  }),

  /**
   * Configuration for the checkbox filters displayed in the filter panel.
   * @see CheckboxFilterPropTypes for detailed documentation.
   */
  checkboxFilter: PropTypes.shape(CheckboxFilterPropTypes),
  /**
   * Unified change handler for both dropdown and checkbox filters.
   * The handler receives a single object with different structures based on the action type:
   *
   * 1. For individual checkbox changes:
   * {
   *   type: "checkbox",              // Indicates this is a checkbox change
   *   checkboxValue: string,         // The value of the checkbox that changed
   *   checked: boolean,              // The new state of the checkbox
   *   isBulkOperation: false,        // Indicates this is changing a single checkbox
   *   updatedCheckboxes: array       // Complete array of checkboxes with updated states
   * }
   *
   * 2. For bulk checkbox operations (e.g. clear all checkboxes):
   * {
   *   type: "checkbox",              // Indicates this is a checkbox change
   *   isBulkOperation: true,         // Indicates this is changing multiple checkboxes
   *   updatedCheckboxes: array       // Complete array of checkboxes with updated states
   * }
   *
   * 3. For dropdown filter changes:
   * {
   *   type: "dropdown",              // Indicates this is a dropdown change
   *   dropdownValues: array,         // The new selected dropdown values
   *   updatedCheckboxes: array       // Current state of all checkboxes
   * }
   *
   * 4. For clearing all filters:
   * {
   *   type: "clear",                 // Indicates this is clearing all filters
   *   isBulkOperation: true,         // Indicates this is a bulk operation
   *   dropdownValues: [],            // Empty array for dropdown values
   *   updatedCheckboxes: array       // Array of checkboxes all set to unchecked
   * }
   */
  onChange: PropTypes.func.isRequired,
};
